"use client";

import { useState, useEffect, useCallback } from "react";

/**
 * Single rule entry for a subcategory (title optional, body required when rule exists).
 */
export interface SubcategoryListingRule {
  title?: string;
  body: string;
}

/**
 * Map of subcategory id -> rule. Empty or missing key = no rules for that subcategory.
 */
export type SubcategoryListingRulesMap = Record<string, SubcategoryListingRule>;

let cachedRules: SubcategoryListingRulesMap | null = null;

/**
 * Call after admin saves listing rules so sell/edit pages get fresh data on next load.
 */
export function invalidateSubcategoryRulesCache(): void {
  cachedRules = null;
}

/**
 * Fetches subcategory listing rules from the public system settings endpoint.
 * Uses in-memory cache to avoid repeated requests.
 */
async function fetchRules(): Promise<SubcategoryListingRulesMap> {
  const res = await fetch("/api/settings/system");
  if (!res.ok) return {};
  const data = (await res.json()) as {
    settings?: {
      subcategory_listing_rules?: SubcategoryListingRulesMap | null;
    };
  };
  const raw = data.settings?.subcategory_listing_rules;
  if (!raw || typeof raw !== "object") return {};
  const map: SubcategoryListingRulesMap = {};
  for (const [id, value] of Object.entries(raw)) {
    if (
      value &&
      typeof value === "object" &&
      typeof (value as SubcategoryListingRule).body === "string"
    ) {
      map[id] = {
        title:
          typeof (value as SubcategoryListingRule).title === "string"
            ? (value as SubcategoryListingRule).title
            : undefined,
        body: (value as SubcategoryListingRule).body,
      };
    }
  }
  return map;
}

export interface UseSubcategoryRulesResult {
  rulesBySubcategoryId: SubcategoryListingRulesMap;
  getRulesForSubcategory: (
    subcategoryId: string,
  ) => SubcategoryListingRule | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Hook to load and cache subcategory listing rules. Use in sell page, edit page, or any screen that needs to show rules.
 */
export function useSubcategoryRules(): UseSubcategoryRulesResult {
  const [rulesBySubcategoryId, setRulesBySubcategoryId] =
    useState<SubcategoryListingRulesMap>(() => cachedRules ?? {});
  const [isLoading, setIsLoading] = useState(!cachedRules);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const map = await fetchRules();
      cachedRules = map;
      setRulesBySubcategoryId(map);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load rules");
      setRulesBySubcategoryId({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const snapshot = cachedRules;
    if (!snapshot) {
      const timer = setTimeout(() => {
        void load();
      }, 0);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => {
      setRulesBySubcategoryId(snapshot);
    }, 0);
    return () => clearTimeout(timer);
  }, [load]);

  const getRulesForSubcategory = useCallback(
    (subcategoryId: string): SubcategoryListingRule | null => {
      if (!subcategoryId) return null;
      const rule = rulesBySubcategoryId[subcategoryId];
      return rule?.body ? rule : null;
    },
    [rulesBySubcategoryId],
  );

  return {
    rulesBySubcategoryId,
    getRulesForSubcategory,
    isLoading,
    error,
    refresh: load,
  };
}
